<template>
	<div
		class="hero-promo"
		:class="{
			'hero-promo--reverse-desktop': reverseContentDesktop,
			'hero-promo--reverse-mobile': reverseContentMobile
		}"
		:style="sectionStyles"
	>
		<div class="hero-promo__container">
			<div class="hero-promo__main-image">
				<img :src="mainImage" alt="">
			</div>
			<div class="hero-promo__content">
				<div class="hero-promo__heading" v-html="title" />
				<div class="hero-promo__subheading" v-html="body" />
				<a
					:href="buttonData.url"
					class="hero-promo__cta"
					:style="buttonStyles"
				>
					{{ buttonData.text }}
				</a>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from "vuex";

export default {
	name: "HeroBanner",
	data() {
		return {
			mobileResolutionBreakpoint: 767
		};
	},
	computed: {
		...mapState({
			heroPromoData: state => state.Home.heroPromo
		}),
		sectionStyles() {
			let {
				background_url: backgroundUrl = "",
				banner_height_desktop: sectionHeight = ""
			} = this.heroPromoData;

			if (this.mobileResolutionBreakpoint >= window.innerWidth) {
				const {
					mobile_background_url: mobileBackgroundUrl = "",
					banner_height_mobile: mobileSectionHeight = ""
				} = this.heroPromoData;

				backgroundUrl = mobileBackgroundUrl;
				sectionHeight = mobileSectionHeight;
			}

			return {
				backgroundImage: `url(${backgroundUrl})`,
				height: `${sectionHeight}px`
			};
		},
		reverseContentDesktop() {
			const { reverse_order_desktop: reverseOrderDesktop = "0" } = this.heroPromoData;

			return Boolean(Number(reverseOrderDesktop));
		},
		reverseContentMobile() {
			const { reverse_order_mobile: reverseOrderMobile = "0" } = this.heroPromoData;

			return Boolean(Number(reverseOrderMobile));
		},
		title() {
			const { title = "" } = this.heroPromoData;

			return title;
		},
		body() {
			const { body = "" } = this.heroPromoData;

			return body;
		},
		mainImage() {
			const { logo_url: mainImage = "" } = this.heroPromoData;

			return mainImage;
		},
		pageButton() {
			const { landing_page_buttons: pageButtons = [] } = this.heroPromoData;
			const [button = {}] = pageButtons;

			return button;
		},
		buttonData() {
			const { text = "", url = "" } = this.pageButton;

			return { text, url };
		},
		buttonStyles() {
			const { bg_color: backgroundColors = [], text_color: textColors = [] } = this.pageButton;
			const [backgroundColor = "", hoverBackground = ""] = backgroundColors;
			const [textColor = "", textHoverColor = ""] = textColors;

			return {
				"--button-background-color": backgroundColor,
				"--button-background-color-hover": hoverBackground,
				"--button-text-color": textColor,
				"--button-text-color-hover": textHoverColor
			};
		}
	}
};
</script>

<style lang="scss">

.hero-promo {
	h1, h2, h3, h4, h5, h6 {
		margin: 0 !important;
	}

	&__content {
		.ql-size-small {
			font-size: 18px;

			@media screen and (max-width: 767px) {
				font-size: 14px;
			}
		}

		.ql-size-large {
			font-size: 48px;

			@media screen and (max-width: 767px) {
				font-size: 32px;
			}
		}

		.ql-size-huge {
			font-size: 65px;

			@media screen and (max-width: 767px) {
				font-size: 46px;
			}
		}
	}
}

</style>

<style lang="scss" scoped>

.hero-promo {
	display: flex;
	align-items: center;
	margin: 0 -15px;
	background-repeat: no-repeat;
	background-size: cover;
	padding: 20px 0;

	@media (min-width: 767px) {
		padding: 0;
	}

	&__container {
		flex-grow: 1;
		margin: 0 auto;
		padding: 0 15px;
		display: flex;
		flex-direction: column;

		@media (min-width: 1100px) {
			max-width: 1170px;
		}

		@media screen and (min-width: 767px) {
			justify-content: space-between;
			flex-direction: row;
		}
	}

	&__main-image {
		max-width: 500px;

		@media screen and (max-width: 767px) {
			max-width: 330px;
			margin: 0 auto;
		}

		@media screen and (min-width: 767px) {
			min-width: 350px;
		}

		img {
			width: 100%;
		}
	}

	&__content {
		text-align: center;

		@media screen and (min-width: 767px) and (max-width: 991px) {
			padding-top: 70px;
		}

		@media screen and (min-width: 767px) {
			max-width: 540px;
			text-align: initial;
		}

		@media screen and (min-width: 991px) {
			padding-top: 120px;
		}
	}

	&__heading {
		display: flex;
		justify-content: center;
		margin-bottom: 15px;
		padding: 0 10px;

		@media screen and (min-width: 767px) {
			padding: 0;
		}
	}

	&__subheading {
		margin-bottom: 30px;

		@media screen and (min-width: 991px) {
			margin-bottom: 60px;
			margin-top: 5px;
		}
	}

	&__cta {
		background-color: var(--button-background-color);
		color: var(--button-text-color);
		display: inline-block;
		font-size: 16px;
		border-radius: 5px;
		font-weight: bold;
		padding: 14px 60px;
		transition: all 0.3s;
		text-align: center;

		@media screen and (max-width: 767px) {
			width: 100%;
		}

		&:hover {
			background-color: var(--button-background-color-hover);
			color: var(--button-text-color-hover);
		}
	}
}

.hero-promo:not(.hero-promo--reverse-desktop) {
	.hero-promo__container {
		@media screen and (min-width: 767px) and (max-width: 1100px) {
			transform: translateX(-10%);
		}
	}

	.hero-promo__main-image {

		@media screen and (max-width: 767px) {
			margin-bottom: 25px;
		}

		@media screen and (min-width: 767px) and (max-width: 1100px) {
			margin-right: 30px;
		}
	}
}

.hero-promo--reverse-desktop {
	.hero-promo__container {
		@media screen and (min-width: 767px) and (max-width: 1100px) {
			transform: translateX(17%);
		}

		@media screen and (min-width: 767px) {
			flex-direction: row-reverse;
		}
	}

	.hero-promo__content {

		@media screen and (min-width: 1100px) and (max-width: 1250px) {
			margin-left: 50px;
		}

		@media screen and (min-width: 991px) {
			padding-top: 140px;
		}

	}

	.hero-promo__main-image {
		margin: 30px auto 0 !important;
	}
}

.hero-promo--reverse-mobile {
	@media (max-width: 767px) {

		.hero-promo__container {
			flex-direction: column-reverse;
		}

		.hero-promo__content {
			padding-top: 50px;
			margin-bottom: 40px;
		}

	}

}
</style>